import { useEffect, useMemo, useRef, useState } from 'react';
import { useAppStore, roleOf, nameOf } from '../store/useAppStore.js';
import { useCallRoom } from '../hooks/useCallRoom.js';
import { useCallChat } from '../hooks/useCallChat.js';
import { useCallExtend } from '../hooks/useCallExtend.js';
import { useCaptions } from '../hooks/useCaptions.js';
import { useSessionTimer } from '../hooks/useSessionTimer.js';
import { SessionMinutesPanel } from './SessionMinutesPanel.js';
import { ExtendSessionModal } from './ExtendSessionModal.js';
import { ExtendRequestPrompt } from './ExtendRequestPrompt.js';
import { ChatPanel } from './ChatPanel.js';
import { CaptionsPanel } from './CaptionsPanel.js';
import { MicOnIcon, MicOffIcon, CamOnIcon, CamOffIcon, HangupIcon, ChatIcon, SplitIcon, PipIcon, ScreenShareIcon, MoveIcon } from '../icons.js';
import { screenCaptureSupported, shouldShowShareButton } from '../lib/screenShare.js';
import { consumeMinutes } from '../api/client.js';
import { pairedRoomName } from '../lib/livekit.js';
import { REAL_MS_PER_CALL_MINUTE } from '../lib/callTiming.js';

const ZERO_HANGUP_DELAY_MS = 10_000;
const CONTROLS_HIDE_MS = 3500;
const MINUTE_TICK_MS = REAL_MS_PER_CALL_MINUTE; // consume one balance-minute per call-minute

// Captions on/off lives here (not in CaptionsPanel) so the in-call CC control
// and the caption band share one source of truth.
const CAPTIONS_ENABLED_KEY = 'aslgo:captions:enabled';

// The stored preference is a desktop-only concept. A phone has no vertical
// space to spare, so captions start off for every call there and a toggle is
// never persisted — CallView mounts fresh per call, so this initial value IS
// the per-call reset. Falls back to desktop when matchMedia is missing, like
// the chatOpen default below.
function isDesktopWidth(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return true;
  return window.matchMedia('(min-width: 1024px)').matches;
}
function loadCaptionsEnabled(): boolean {
  if (typeof window === 'undefined') return true;
  if (!isDesktopWidth()) return false;
  return window.localStorage.getItem(CAPTIONS_ENABLED_KEY) !== 'false';
}

// Tapping the self-view walks these in order and wraps. The bottom offsets are
// the tuned control-pill clearance — the pill grows taller at `sm` when its
// ControlButton labels render — so do not re-tune them. The top offsets clear
// the guide's "In session with" chip (~36-40px) and the "speaking" badge
// (~25-30px), both anchored at top-4, with real margin to spare. They also
// clear the learner's minute pill in its healthy state — but NOT once
// sessionMinutes <= 5, when SessionMinutesPanel swaps in the full low-balance
// card (max-h-52 package list) at the same top-4 right-4 anchor. That card is
// top-4 right-4 with max-w-[min(90vw,22rem)], so at shrink-to-fit width on a
// 390px phone it spans roughly x=85 to the right edge and is ~330px tall —
// it fully covers a thumbnail parked top-right, and also reaches across the
// right half of one parked top-left (top-left keeps a tappable strip along
// its left edge; top-right does not). That overlap is deliberate: the panel
// is z-20 and the thumbnail is z-10, so the low-balance warning always wins
// over the self-view in both corners. Do not add avoidance logic for it.
type PipCorner = 'bottom-right' | 'bottom-left' | 'top-left' | 'top-right';
const PIP_CORNERS: PipCorner[] = ['bottom-right', 'bottom-left', 'top-left', 'top-right'];
const PIP_CORNER_CLASS: Record<PipCorner, string> = {
  'bottom-right': 'bottom-24 sm:bottom-28 right-4 sm:right-6',
  'bottom-left':  'bottom-24 sm:bottom-28 left-4 sm:left-6',
  'top-left':     'top-16 sm:top-20 left-4 sm:left-6',
  'top-right':    'top-16 sm:top-20 right-4 sm:right-6',
};
const PIP_CORNER_LABEL: Record<PipCorner, string> = {
  'bottom-right': 'bottom right',
  'bottom-left':  'bottom left',
  'top-left':     'top left',
  'top-right':    'top right',
};

export function CallView() {
  const userId = useAppStore((s) => s.userId);
  const displayName = useAppStore((s) => s.displayName);
  const callPeer = useAppStore((s) => s.callPeer);
  const users = useAppStore((s) => s.users);
  const sessionMinutes = useAppStore((s) => s.sessionMinutes);
  const setSessionMinutes = useAppStore((s) => s.setSessionMinutes);
  const callDurationMinutes = useAppStore((s) => s.callDurationMinutes);
  const isLearner = roleOf(users, userId) === 'learner';

  const { room, localVideo, remoteVideo, remoteAudio, remoteIdentity, remoteName, localScreen, remoteScreen, screenShareEnabled, micEnabled, camEnabled, toggleMic, toggleCam, toggleScreenShare, hangup } = useCallRoom();
  const connected = remoteIdentity !== null;
  const timer = useSessionTimer({ durationMinutes: callDurationMinutes, active: connected });
  // Mirror the remaining time to the store so the top menu bar can show the
  // countdown (the timer lives here, but the TopBar is rendered up in App).
  const setSessionCountdown = useAppStore((s) => s.setSessionCountdown);
  useEffect(() => {
    setSessionCountdown(connected && callDurationMinutes != null ? timer.remainingSeconds : null);
  }, [connected, callDurationMinutes, timer.remainingSeconds, setSessionCountdown]);
  const { messages, send } = useCallChat(room, userId);
  const extend = useCallExtend({ room, userId, isLearner, onApproved: (n) => timer.extend(n) });

  const captions = useCaptions(room);
  // callPeer and remoteIdentity are uuids (keys); every rendered peer name goes
  // through this one label. The name LiveKit carries on the peer's token wins —
  // it is minted at join, so it beats this client's roster snapshot. Then the
  // roster, tried against both ids: remoteIdentity is the participant actually
  // publishing, and callPeer still resolves before they do (and if the
  // publisher turns out to be someone the roster doesn't know).
  const peerName = remoteName ?? nameOf(users, remoteIdentity) ?? nameOf(users, callPeer);
  // CaptionsPanel expects names keyed by role, not by local/remote.
  const guideName = isLearner ? peerName : displayName;
  const learnerName = isLearner ? displayName : peerName;
  const localSpeaking = isLearner ? captions.speaking.learner : captions.speaking.guide;
  const remoteSpeaking = isLearner ? captions.speaking.guide : captions.speaking.learner;

  const localRef = useRef<HTMLVideoElement>(null);
  const remoteRef = useRef<HTMLVideoElement>(null);
  // Wake-the-controls listeners live on the whole call area, not just the
  // stage: the caption band is now a sibling row BELOW the stage and it owns
  // the bottom strip of a phone screen, where a thumb rests.
  const callAreaRef = useRef<HTMLDivElement>(null);
  const screenRef = useRef<HTMLVideoElement>(null);

  // Layout: PIP (one large remote video + small self-view overlay) vs split
  // (both videos equal halves of the stage). Both <video> elements stay mounted
  // across the toggle — only their CSS rects change — so the LiveKit tracks
  // never detach. Resets to PIP each call.
  const [splitView, setSplitView] = useState(false);

  // Screen share takes precedence over the PIP/Split toggle. The displayed
  // screen track is local while WE share, otherwise the remote's (one at a time).
  const screenTrack = localScreen ?? remoteScreen;
  const screenShareActive = screenTrack !== null;
  // Reset per call by construction: CallView mounts fresh for each call.
  const [pipCorner, setPipCorner] = useState<PipCorner>('bottom-right');
  const nextPipCorner = PIP_CORNERS[(PIP_CORNERS.indexOf(pipCorner) + 1) % PIP_CORNERS.length];
  const floatingPip = !splitView && !screenShareActive;
  const captureSupported = useMemo(() => screenCaptureSupported(), []);
  const showShareButton = shouldShowShareButton({
    supported: captureSupported,
    remoteSharing: remoteScreen !== null,
  });

  // Remote-camera <video> placement across the three layouts. In screen-share
  // mode the camera takes the bottom/right half (the screen takes top/left).
  const remoteVideoClassName = screenShareActive
    ? 'absolute left-0 bottom-0 w-full h-1/2 sm:left-1/2 sm:top-0 sm:w-1/2 sm:h-full object-contain bg-black'
    : splitView
      ? 'absolute left-0 top-0 w-full h-1/2 sm:w-1/2 sm:h-full object-contain bg-black'
      : 'absolute inset-0 w-full h-full object-contain bg-black';

  // Self-view container placement: small PIP in the camera pane's lower-right
  // for screen share (the stage's bottom-right corner IS the camera pane's
  // lower-right in both orientations); half-pane for split; floating PIP otherwise.
  const selfViewClassName = screenShareActive
    ? 'bottom-4 right-4 w-28 sm:w-40 lg:w-48 aspect-[4/3] rounded-xl shadow-chrome'
    : splitView
      ? 'inset-x-0 bottom-0 h-1/2 sm:left-1/2 sm:h-full'
      // PIP bottom offset clears the control pill: the pill sits at bottom-7
      // (28px) and from `sm` up its ControlButton text labels render, growing
      // it to ~74px tall (top edge 102px, past the PIP's 96px). 112px
      // restores the gap.
    : `${PIP_CORNER_CLASS[pipCorner]} w-32 sm:w-40 lg:w-48 aspect-[4/3] rounded-xl shadow-chrome`;

  const selfVideoFit = splitView && !screenShareActive ? 'object-contain' : 'object-cover';

  // Chat visibility + unread count tracking. One flag drives both surfaces:
  // the desktop rail (`lg:` markup) and the mobile drawer (`lg:hidden` markup),
  // so whichever breakpoint is active renders the right one. Default open on
  // desktop (the rail is part of the layout there) and closed on mobile.
  const [chatOpen, setChatOpen] = useState(isDesktopWidth);
  const [lastSeenCount, setLastSeenCount] = useState(0);
  const unreadCount = Math.max(0, messages.length - lastSeenCount);

  // Shared captions on/off (CC control in the bar + the band's CC button).
  const [captionsEnabled, setCaptionsEnabled] = useState(loadCaptionsEnabled);
  useEffect(() => {
    if (!isDesktopWidth()) return;
    window.localStorage.setItem(CAPTIONS_ENABLED_KEY, String(captionsEnabled));
  }, [captionsEnabled]);
  const toggleCaptions = () => setCaptionsEnabled((v) => !v);
  // Reset unread whenever chat is visible.
  useEffect(() => {
    if (chatOpen) setLastSeenCount(messages.length);
  }, [chatOpen, messages.length]);

  // Auto-open chat when a message arrives from the peer (mobile drawer or the
  // desktop rail, whichever is active for the current breakpoint).
  const prevMessagesLen = useRef(messages.length);
  useEffect(() => {
    const prev = prevMessagesLen.current;
    prevMessagesLen.current = messages.length;
    if (messages.length <= prev) return;
    const newest = messages[messages.length - 1];
    if (newest && newest.from !== userId) setChatOpen(true);
  }, [messages, userId]);

  // Controls auto-hide. Show on mount, on any pointer movement / touch,
  // hide after CONTROLS_HIDE_MS of inactivity. Always visible on hover of the
  // pill itself (handled separately).
  const [controlsVisible, setControlsVisible] = useState(true);
  const hideTimerRef = useRef<number | null>(null);
  useEffect(() => {
    function show() {
      setControlsVisible(true);
      if (hideTimerRef.current) window.clearTimeout(hideTimerRef.current);
      hideTimerRef.current = window.setTimeout(() => setControlsVisible(false), CONTROLS_HIDE_MS);
    }
    show();
    const area = callAreaRef.current;
    if (!area) return () => {};
    area.addEventListener('mousemove', show);
    area.addEventListener('touchstart', show);
    area.addEventListener('keydown', show);
    return () => {
      area.removeEventListener('mousemove', show);
      area.removeEventListener('touchstart', show);
      area.removeEventListener('keydown', show);
      if (hideTimerRef.current) window.clearTimeout(hideTimerRef.current);
    };
  }, []);

  // useCallRoom returns a fresh hangup() on every render; route through a ref
  // so the auto-hangup effect doesn't re-arm its timer every render.
  const hangupRef = useRef(hangup);
  useEffect(() => { hangupRef.current = hangup; });

  useEffect(() => {
    if (!isLearner) return;
    if (sessionMinutes > 0) return;
    const id = setTimeout(() => { void hangupRef.current(); }, ZERO_HANGUP_DELAY_MS);
    return () => clearTimeout(id);
  }, [isLearner, sessionMinutes]);

  // Auto-end when the requested session length elapses (parallels the
  // balance-0 hangup above). Applies to both roles.
  useEffect(() => {
    if (!timer.ended) return;
    const id = setTimeout(() => { void hangupRef.current(); }, ZERO_HANGUP_DELAY_MS);
    return () => clearTimeout(id);
  }, [timer.ended]);

  // Clear the call's requested duration once this view unmounts, so a stale
  // value doesn't leak into the next call before it's re-populated.
  useEffect(() => () => { useAppStore.getState().setCallDuration(null); useAppStore.getState().setSessionCountdown(null); }, []);

  // 10-minute warning → extend-session modal (learner only). Opens once at
  // the warning threshold; a ref guards it so dismissing ("Not now") doesn't
  // immediately reopen it while `timer.warning` is still true.
  const [showExtend, setShowExtend] = useState(false);
  const extendShownRef = useRef(false);
  useEffect(() => {
    if (!isLearner) return;
    if (timer.warning && !extendShownRef.current) {
      extendShownRef.current = true;
      setShowExtend(true);
    }
    if (!timer.warning) extendShownRef.current = false;
  }, [isLearner, timer.warning]);

  // Learner minute consumption. Lives here (not in SessionMinutesPanel) so the
  // ticker keeps running as a single source while the panel is re-placed
  // between the stage overlay and the chat rail. Only ticks once the peer is
  // publishing, so the ringing/connecting window doesn't burn a minute.
  useEffect(() => {
    if (!isLearner || !userId || !callPeer || remoteIdentity === null) return;
    const room = pairedRoomName(userId, callPeer);
    const id = setInterval(() => {
      void consumeMinutes({ amount: 1, room })
        .then((res) => setSessionMinutes(res.sessionMinutes))
        .catch(() => { /* network blip — keep the call running */ });
    }, MINUTE_TICK_MS);
    return () => clearInterval(id);
  }, [isLearner, userId, callPeer, remoteIdentity, setSessionMinutes]);

  useEffect(() => {
    if (localVideo && localRef.current) localVideo.attach(localRef.current);
    return () => { if (localVideo && localRef.current) localVideo.detach(localRef.current); };
  }, [localVideo]);

  useEffect(() => {
    if (remoteVideo && remoteRef.current) {
      remoteVideo.attach(remoteRef.current);
      remoteRef.current.play().catch(() => {});
    }
    return () => { if (remoteVideo && remoteRef.current) remoteVideo.detach(remoteRef.current); };
  }, [remoteVideo]);

  useEffect(() => {
    if (screenTrack && screenRef.current) {
      screenTrack.attach(screenRef.current);
      screenRef.current.play()?.catch(() => {});
    }
    return () => { if (screenTrack && screenRef.current) screenTrack.detach(screenRef.current); };
  }, [screenTrack]);

  useEffect(() => {
    if (!remoteAudio) return;
    const el = remoteAudio.attach();
    document.body.appendChild(el);
    return () => { remoteAudio.detach(el); el.remove(); };
  }, [remoteAudio]);

  return (
    <div ref={callAreaRef} className="flex-1 flex flex-col bg-black text-white relative min-h-0">
      <div className="flex flex-1 min-h-0">
      {/* Video stage */}
      <div className="relative flex-1 min-w-0 has-grain bg-black overflow-hidden">
        {/* Remote video. PIP: full-bleed, contained so we never crop a face,
         *  centered, so a portrait phone camera fills the stage and a 16:9
         *  webcam letterboxes evenly. Split: takes half the stage — top half when
         *  stacked (portrait/narrow), left half when side-by-side (≥sm).
         *  Screen share: takes the camera half opposite the shared screen —
         *  bottom half when stacked, right half side-by-side — with the
         *  self-view as a PIP in this pane's lower-right. */}
        <video
          ref={remoteRef}
          autoPlay
          playsInline
          className={remoteVideoClassName}
        />

        {/* Shared screen — mounts only while a share is active; left/top half,
         *  object-contain so a slide or app is never cropped. */}
        {screenShareActive ? (
          <video
            ref={screenRef}
            autoPlay
            playsInline
            aria-label="Shared screen"
            className="absolute left-0 top-0 w-full h-1/2 sm:w-1/2 sm:h-full object-contain bg-black"
          />
        ) : null}

        {remoteVideo && remoteSpeaking ? (
          <div className="absolute top-4 left-4 z-10 px-2.5 py-1 rounded-full bg-emerald-500/90 text-black text-[0.7rem] font-medium animate-fade-in flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-black animate-pulse" />
            speaking
          </div>
        ) : null}

        {/* Empty / connecting state */}
        {!remoteVideo ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-center px-6 animate-fade-in">
            <div className="text-[0.7rem] uppercase tracking-[0.2em] text-ink-400">Connecting</div>
            <p className="text-2xl font-display font-medium text-ink-200">
              Waiting for {peerName ?? 'your peer'}…
            </p>
            <div className="flex gap-1.5 mt-2">
              <span className="w-2 h-2 rounded-full bg-white/60 animate-typing" style={{ animationDelay: '0s' }} />
              <span className="w-2 h-2 rounded-full bg-white/60 animate-typing" style={{ animationDelay: '0.2s' }} />
              <span className="w-2 h-2 rounded-full bg-white/60 animate-typing" style={{ animationDelay: '0.4s' }} />
            </div>
          </div>
        ) : null}

        {/* Minute pill (learner only) — floats top-right, out of the centered
         *  signing space. On desktop it hides while the chat rail is open, where
         *  it reappears stacked above the chat box instead. */}
        {isLearner ? (
          <SessionMinutesPanel
            className={`absolute top-4 right-4 z-20 max-w-[min(90vw,22rem)] ${chatOpen || showExtend ? 'lg:hidden' : ''}`}
          />
        ) : null}

        {/* Top-right: peer name + role (guide-side context, since they don't have the pill) */}
        {!isLearner ? (
          <div className="absolute top-4 right-4 z-10 px-3.5 py-2 rounded-full bg-white/[0.08] backdrop-blur ring-1 ring-white/10 text-[0.85rem] flex items-center gap-2 animate-fade-in">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse-soft" />
            <span className="text-white/90">In session with <span className="font-medium">{peerName ?? '—'}</span></span>
          </div>
        ) : null}

        {/* Learner-only: shown while an extend request is out for guide
         *  approval (or has timed out silently — awaiting just clears).
         *  Three-layer stack, front to back: SessionMinutesPanel (z-20,
         *  the low-balance warning must always win) > this pill (z-[15])
         *  > self-view thumbnail (z-10). This has to sit strictly between
         *  the two — z-20 would tie with the minute panel and, since this
         *  pill is declared later in the DOM, win the tie and bury the
         *  low-balance warning it's supposed to defer to. No named z-index
         *  step exists between 10 and 20, hence the arbitrary value; don't
         *  "tidy" this back to z-20. pointer-events-none because it's a
         *  passive notice with no controls, so it must never intercept
         *  taps meant for whatever it happens to overlap. */}
        {isLearner && extend.awaiting ? (
          <div className="absolute top-16 left-1/2 -translate-x-1/2 z-[15] pointer-events-none px-3 py-1.5 rounded-full bg-white/[0.08] backdrop-blur ring-1 ring-white/10 animate-fade-in">
            <span className="text-[0.8rem] text-ink-300">Waiting for the guide to approve…</span>
          </div>
        ) : null}

        {/* Persistent share indicator — the main controls auto-hide, so keep a
         *  reachable Stop while the local user is sharing. */}
        {screenShareEnabled ? (
          <div className="absolute top-16 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2
                          px-3 py-1.5 rounded-full bg-brand-bright/90 text-ink-900 text-[0.8rem] font-medium
                          shadow-chrome animate-fade-in">
            <span className="w-1.5 h-1.5 rounded-full bg-ink-900 animate-pulse" />
            You're sharing your screen
            <button
              type="button"
              onClick={() => { void toggleScreenShare(); }}
              className="ml-1 px-2 py-0.5 rounded-full bg-ink-900/15 hover:bg-ink-900/25 transition text-ink-900"
            >
              Stop
            </button>
          </div>
        ) : null}

        {/* Self-view. PIP: small overlay that starts in the stage's bottom-right
         *  on every breakpoint (bottom-right rather than top-right because the
         *  top corners hold the minute pill and the "In session with" chip),
         *  raised clear of the control pill — tap it to walk it between the
         *  four corners. Split: a flush half-pane — bottom half when stacked
         *  (portrait/narrow), right half when side-by-side (≥sm). The ring
         *  doubles as a seam divider between the two panes in split mode. */}
        <div className={`absolute z-10 overflow-hidden bg-black animate-fade-in
                        ${localSpeaking ? 'ring-2 ring-emerald-400' : 'ring-1 ring-white/15'}
                        ${selfViewClassName}`}>
          <video ref={localRef} autoPlay muted playsInline className={`w-full h-full ${selfVideoFit}`} />
          <div className="absolute top-1.5 left-1.5 px-1.5 py-0.5 rounded bg-black/55 backdrop-blur text-[0.62rem] uppercase tracking-wider text-white/80">
            You
          </div>
          {!camEnabled ? (
            <div className="absolute inset-0 flex items-center justify-center bg-black/85 text-ink-300">
              <CamOffIcon />
            </div>
          ) : null}
          {floatingPip ? (
            <>
              <button
                type="button"
                onClick={() => setPipCorner(nextPipCorner)}
                aria-label={`Your video, ${PIP_CORNER_LABEL[pipCorner]}. Move to ${PIP_CORNER_LABEL[nextPipCorner]}.`}
                className="absolute inset-0 z-10 focus-visible:outline-none
                           focus-visible:ring-2 focus-visible:ring-white/60 focus-visible:ring-inset"
              />
              {/* The fade goes on the hint, never the button: .controls-hidden
               *  sets pointer-events: none, which would make the thumbnail
               *  untappable a few seconds into every call. */}
              <span
                aria-hidden
                data-testid="pip-move-hint"
                className={`controls-fade ${controlsVisible ? '' : 'controls-hidden'}
                            absolute bottom-1.5 right-1.5 z-20 p-1 rounded pointer-events-none
                            bg-black/55 backdrop-blur text-white/80`}
              >
                <MoveIcon width={14} height={14} />
              </span>
            </>
          ) : null}
        </div>

        {/* Auto-hide controls. The pill sits at the bottom of the stage in every
         *  layout and orientation — the split/screen-share special case is gone
         *  because there is no longer an empty band to tuck it into. */}
        <div
          className={`controls-fade ${controlsVisible ? '' : 'controls-hidden'}
                      absolute z-20 left-1/2 -translate-x-1/2 bottom-7`}
        >
          {/* Hangup is split out of the main pill into its own button with a
           *  visible gap, so a mis-tap near the chat button doesn't end the
           *  call by accident. */}
          <div className="flex items-end gap-10 sm:gap-12">
            <div className="flex items-center gap-1.5 sm:gap-2 px-3 py-2 rounded-2xl
                            bg-white/[0.08] backdrop-blur-xl ring-1 ring-white/[0.08] shadow-chrome">
              <ControlButton
                active={micEnabled}
                onClick={() => { void toggleMic(); }}
                label={micEnabled ? 'Mute' : 'Unmute'}
              >
                {micEnabled ? <MicOnIcon /> : <MicOffIcon />}
              </ControlButton>
              <ControlButton
                active={camEnabled}
                onClick={() => { void toggleCam(); }}
                label="Video"
                ariaLabel={camEnabled ? 'Stop video' : 'Start video'}
              >
                {camEnabled ? <CamOnIcon /> : <CamOffIcon />}
              </ControlButton>

              {/* Chat toggle — opens/closes the mobile drawer or the desktop rail. */}
              <ControlButton
                onClick={() => setChatOpen((open) => !open)}
                label="Chat"
                ariaLabel={`${chatOpen ? 'Close' : 'Open'} chat${unreadCount ? ` (${unreadCount} new)` : ''}`}
                badge={unreadCount > 0 ? (
                  <span className="absolute -top-1 -right-1 min-w-[1.25rem] h-5 px-1.5 rounded-full
                                   bg-brand-bright text-ink-900 text-[0.65rem] font-semibold leading-none
                                   flex items-center justify-center ring-2 ring-black">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                ) : undefined}
              >
                <ChatIcon />
              </ControlButton>

              {/* Screen-share toggle — only where capture is supported and the
               *  peer isn't already sharing (one at a time). Active tint while sharing. */}
              {showShareButton ? (
                <ControlButton
                  onClick={() => { void toggleScreenShare(); }}
                  label="Share"
                  ariaLabel={screenShareEnabled ? 'Stop sharing your screen' : 'Share your screen'}
                  highlight={screenShareEnabled}
                >
                  <ScreenShareIcon />
                </ControlButton>
              ) : null}

              {/* Captions toggle — shares state with the caption band. */}
              <ControlButton
                onClick={toggleCaptions}
                label="Captions"
                ariaLabel={captionsEnabled ? 'Hide captions' : 'Show captions'}
                highlight={captionsEnabled}
              >
                <span className="text-[0.82rem] font-bold tracking-tight">CC</span>
              </ControlButton>

              {/* Layout toggle — PIP ⇄ split. Hidden while screen sharing,
               *  which owns the layout. */}
              {!screenShareActive ? (
                <ControlButton
                  onClick={() => setSplitView((s) => !s)}
                  label={splitView ? 'PiP' : 'Split'}
                  ariaLabel={splitView ? 'Picture-in-picture' : 'Split video'}
                >
                  {splitView ? <PipIcon /> : <SplitIcon />}
                </ControlButton>
              ) : null}
            </div>

            <ControlButton
              variant="danger"
              onClick={() => { void hangup(); }}
              label="End"
              ariaLabel="End call"
            >
              <HangupIcon />
            </ControlButton>
          </div>
        </div>

      </div>

      {/* Desktop chat rail — toggled via the chat button or its own close control.
       *  For the learner the minute pill sits at the top of this rail, so the
       *  chat box lives directly below the countdown. */}
      {chatOpen || (isLearner && showExtend) ? (
        <aside className="hidden lg:flex w-[340px] xl:w-[380px] flex-col min-h-0 border-l border-white/[0.06]">
          {isLearner ? <SessionMinutesPanel className="p-3 shrink-0" /> : null}
          {isLearner && showExtend ? (
            <div className="shrink-0 p-3 border-b border-white/[0.06] overflow-y-auto">
              <ExtendSessionModal
                onPickBlock={(n) => { setShowExtend(false); extend.requestExtend(n); }}
                onClose={() => setShowExtend(false)}
              />
            </div>
          ) : null}
          {chatOpen ? (
            <div className="flex-1 min-h-0">
              <ChatPanel
                messages={messages}
                selfUserId={userId}
                peerName={peerName}
                onSend={send}
                onClose={() => setChatOpen(false)}
              />
            </div>
          ) : null}
        </aside>
      ) : null}
      </div>

      {/* Mobile caption band — a sibling row below the stage, sized by its own
       *  content, rendered only while captions are on so the video gets the
       *  whole stage otherwise. Shown in split and screen-share modes too: it
       *  no longer overlays the video, so there is nothing to conflict with. */}
      {captionsEnabled ? (
        <CaptionsPanel
          compact
          data-testid="mobile-captions"
          // Hard ceiling so no future content change can starve the stage. The
          // band's natural height is ~122px (py-3 + two 2.75rem windows +
          // space-y-2.5); 9rem leaves headroom for a taller default line box
          // while still capping it, and overflow-hidden makes the cap bite.
          className="lg:hidden shrink-0 max-h-[9rem] overflow-hidden"
          learner={captions.learner}
          guide={captions.guide}
          speaking={captions.speaking}
          micOff={captions.micOff}
          guideName={guideName}
          learnerName={learnerName}
          enabled={captionsEnabled}
          onToggleEnabled={toggleCaptions}
        />
      ) : null}

      {/* Desktop captions — full-width row below stage + chat rail. */}
      <CaptionsPanel
        className="hidden lg:block"
        learner={captions.learner}
        guide={captions.guide}
        speaking={captions.speaking}
        micOff={captions.micOff}
        guideName={guideName}
        learnerName={learnerName}
        enabled={captionsEnabled}
        onToggleEnabled={toggleCaptions}
      />

      {/* Mobile drawer */}
      {chatOpen ? (
        <>
          <button
            type="button"
            aria-label="Close chat"
            onClick={() => setChatOpen(false)}
            className="lg:hidden fixed inset-0 z-40 bg-black/55 backdrop-blur-sm animate-fade-in"
          />
          {/* Compact chat window anchored to the bottom of the viewport, covering
           *  the lower half of the screen, independent of video height. */}
          <div className="lg:hidden fixed inset-x-2 z-50 rounded-2xl overflow-hidden
                          shadow-chrome animate-fade-up
                          bottom-4 h-[45dvh]">
            <ChatPanel
              messages={messages}
              selfUserId={userId}
              peerName={peerName}
              onSend={send}
              drawer
              onClose={() => setChatOpen(false)}
            />
          </div>
        </>
      ) : null}

      {/* Learner-only end-of-session extend prompt. Desktop renders it in the
       *  chat rail under the balance pill (above); this is the MOBILE top sheet
       *  (not a center overlay). Pick a block → request the guide's approval. */}
      {showExtend ? (
        <div className="lg:hidden fixed inset-x-2 top-[calc(3.5rem+0.5rem)] z-50 animate-fade-up">
          <ExtendSessionModal
            onPickBlock={(n) => { setShowExtend(false); extend.requestExtend(n); }}
            onClose={() => setShowExtend(false)}
          />
        </div>
      ) : null}

      {/* Guide-side Accept/Decline prompt for an incoming extend request. */}
      {!isLearner && extend.pendingRequest ? (
        <ExtendRequestPrompt minutes={extend.pendingRequest.minutes} onRespond={extend.respond} />
      ) : null}
    </div>
  );
}

/* --------------------------------------------------------------------------
 * ControlButton — round icon in a circle with a label beneath (labels show on
 * sm+ only, so the mobile layout math is unchanged). Flavours: default, danger
 * (red, for hangup), and `highlight` (gold fill, for active toggles like CC /
 * screen-share). `badge` renders in the circle's top-right (chat unread count).
 * ------------------------------------------------------------------------ */
function ControlButton({
  children,
  onClick,
  variant = 'default',
  label,
  ariaLabel,
  highlight = false,
  badge,
}: {
  children: React.ReactNode;
  onClick: () => void;
  variant?: 'default' | 'danger';
  label: string;
  ariaLabel?: string;
  highlight?: boolean;
  badge?: React.ReactNode;
  active?: boolean;
}) {
  const circle = variant === 'danger'
    ? 'bg-status-error hover:bg-red-700 text-white'
    : highlight
      ? 'bg-brand-bright text-ink-900'
      : 'bg-white/10 hover:bg-white/20 text-white';
  const labelTone = variant === 'danger'
    ? 'text-status-errorBright'
    : highlight
      ? 'text-brand-bright'
      : 'text-ink-400';
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={ariaLabel ?? label}
      title={ariaLabel ?? label}
      className="group flex flex-col items-center gap-1 focus-visible:outline-none"
    >
      <span
        className={`relative w-11 h-11 rounded-full flex items-center justify-center transition ${circle}
                    group-focus-visible:ring-2 group-focus-visible:ring-white/40`}
      >
        {children}
        {badge}
      </span>
      <span className={`hidden sm:block text-[0.62rem] font-medium leading-none ${labelTone}`}>{label}</span>
    </button>
  );
}
